import { useMemo } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { bandLabel, type IntensityGrid } from "@jackedlogs/shared";

import type { ConfirmFn } from "../seams";
import { resolveTheme } from "../theme";
import { useThemePreferenceContext } from "../ThemeContext";
import { ScaleLegend } from "../primitives/ScaleLegend";
import { GridCell } from "./GridCell";
import { GridStructureRow } from "./GridStructureRow";
import { makeGridStyles } from "./gridStyles";
import { useGridGestures } from "./useGridGestures";

// The day × week heat grid, lifted from the mobile Home program preview
// (v3.2.0) into the shared library so the composer's live preview and
// Home render the SAME glance. Callers build the grid
// (buildIntensityGrid from a stored Program, or
// buildIntensityGridFromDraft from the editor draft) and own the
// selection state.
// Every cell shows its value (Antenore 2026-06-11: the whole program's
// shape must read without tapping row by row). Cells with prescribed work
// but no banded intensity (kind none/history/kg) render outlined with the
// set count ("18×") — a free day must never look like a rest day.
//
// ⭐ PE-27 (2026-08-28) — THIS IS NOW THE ONE GRID. It absorbed
// `PlacementMatrix`, which is gone: the prescribe editor showed a placement
// matrix stacked on top of this one, two grids of the same shape asking the
// reader to hold both. They differed in ORIENTATION (that one was weeks ×
// days) and in data plane, not in job.
// ⛔ And it was NOT this component that rotated: it already had days as rows
// and weeks as columns, which is the orientation the locked design asks for.
// The other one was the transposed one, and it is the one that died.
//
// Everything the editor needs is OPTIONAL, and absent means exactly the
// read-only glance Home has always rendered:
//   · `selectedWeek` absent  ⇒ no cursor ring is ever painted.
//   · `onSelectCell` absent  ⇒ a cell tap selects its DAY, which is what
//     tapping the row did before — Home's behaviour, unchanged.
//   · `placement` absent     ⇒ no ✓ layer, no long-press, nothing spoken.
//   · the structural handles absent ⇒ no add/remove row at all.
// Home passes none of them (ProgramPreview.tsx), so its mount is untouched
// by construction rather than by a branch someone has to remember.
/**
 * The focused exercise's placement layer. Lives here because the grid is now
 * the only thing that renders it — `usePrescribePlacement` and `useComposer`
 * both produce this shape, and before PE-27 it was typed off the dead
 * `PlacementMatrix`.
 */
export type GridPlacement = {
  label: string;
  isPlaced: (dayIdx: number, week: number) => boolean;
  onToggle: (dayIdx: number, week: number) => void;
};

export function IntensityGridView({
  grid,
  selected,
  onSelect,
  dark,
  title,
  selectedWeek = null,
  onSelectCell,
  placement,
  onAddDay,
  onAddWeek,
  onRemoveWeek,
  onRemoveDay,
  confirm,
}: {
  grid: IntensityGrid;
  selected: number | null;
  onSelect: (sessionIdx: number) => void;
  dark: boolean;
  // Section label above the grid; defaults to the Home preview's title.
  // Pass null to render none (the composer has its own section header).
  title?: string | null;
  /** The cursor's week. Null (Home) ⇒ no cell ever reads as the cursor. */
  selectedWeek?: number | null;
  /** Cell tap in the editor: move the cursor to (day, week). */
  onSelectCell?: (dayIdx: number, week: number) => void;
  /**
   * The focused exercise's placement layer: is it on (day, week), and the
   * write that toggles it. Null when no exercise is focused — the grid is
   * then navigation-only, exactly as the composer's matrix was.
   */
  placement?: GridPlacement | null;
  onAddDay?: () => void;
  onAddWeek?: () => void;
  onRemoveWeek?: (week: number) => void;
  onRemoveDay?: (dayIdx: number) => void;
  /**
   * PE-29 — the platform's confirm, required for either removal: a week or a
   * day is dropped by HOLDING its own header, and a hold has no armed state to
   * take back, so the dialog is the only step between the gesture and the loss.
   * ⛔ Absent ⇒ nothing is holdable and the hint never claims it is.
   */
  confirm?: ConfirmFn;
}) {
  const { t } = useTranslation();
  const family = useThemePreferenceContext()?.preference.family ?? "brand";
  const theme = resolveTheme(dark, family);
  const canRemoveWeek = confirm != null && onRemoveWeek != null && grid.weeks > 1;
  const canRemoveDay =
    confirm != null && onRemoveDay != null && grid.days.length > 1;
  // The week head grows to the 44pt floor only where it IS a control — Home
  // passes no remover and keeps the 20pt label row it has always had.
  const styles = useMemo(
    () => makeGridStyles(theme, canRemoveWeek),
    [theme, canRemoveWeek],
  );

  const heading =
    title === undefined
      ? t("mobile.preview.gridTitle", { defaultValue: "Intensity · day × week" })
      : title;

  // No cursor on screen ⇒ the ring is the ROW's (Home). With a cursor, the
  // ring moves to the cell and the row keeps tint + brand number.
  const rowRing = selectedWeek == null;
  // A cell is a control only where the editor gave it something to do. On Home
  // it stays pressable (tapping the heat picks the day) but stops being an
  // accessibility stop — see GridCell.
  const cellsInteractive = onSelectCell != null || placement != null;

  const hasStructure =
    onAddDay != null || onAddWeek != null || canRemoveWeek || canRemoveDay;

  const { weekRemoval, dayRemoval, cellPlacement } = useGridGestures({
    t,
    confirm,
    canRemoveWeek,
    canRemoveDay,
    onRemoveWeek,
    onRemoveDay,
    placement,
  });

  return (
    <View>
      {heading ? <Text style={styles.sectionLabel}>{heading}</Text> : null}

      {/* The pinned day column and the scrolling body. ⭐ ONE ScrollView: the
          week header rides inside it with the cell rows, so there is no second
          scroller to keep in step and the header can never drift from its
          columns. */}
      <View style={styles.gridFrame}>
        <View style={styles.gridPinnedCol}>
          <View style={styles.gridPinnedHeadSpacer} />
          {grid.days.map((day) => {
            const isSel = day.sessionIdx === selected;
            return (
              <Pressable
                key={day.sessionIdx}
                accessibilityRole="button"
                accessibilityState={{ selected: isSel }}
                accessibilityLabel={`${day.name}${day.band ? ` · ${bandLabel(t, day.band)}` : ""}`}
                accessibilityHint={
                  canRemoveDay
                    ? t("composer.holdDayToRemoveHint", {
                        defaultValue: "Hold a day to remove it",
                      })
                    : undefined
                }
                onPress={() => onSelect(day.sessionIdx)}
                {...dayRemoval(day.sessionIdx)}
                style={({ pressed }) => [
                  styles.gridRow,
                  isSel && styles.gridRowSelected,
                  isSel && rowRing && styles.gridRowSelectedRing,
                  isSel && rowRing && styles.gridRowSelectedPinned,
                  pressed && styles.gridRowPressed,
                ]}
              >
                <View
                  style={[
                    styles.gridDayNumBox,
                    isSel && !rowRing && styles.gridDayNumBoxSelected,
                  ]}
                >
                  {/* PE-29 — "i giorni devono essere D1, D2, ..., Dx"
                      (Antenore, smoke 28/08). The bare ordinal sat under a
                      row of W1…Wn and read as a count, not as a coordinate;
                      the rest of the editor has always spoken in D-numbers
                      (the day − said "− D3", the placement switch says
                      "D2 · W1"). One notation now, everywhere. */}
                  <Text
                    style={[
                      styles.gridDayNum,
                      isSel &&
                        (rowRing
                          ? styles.gridDayNumSelectedRing
                          : styles.gridDayNumSelected),
                    ]}
                  >
                    {`D${day.sessionIdx + 1}`}
                  </Text>
                </View>
              </Pressable>
            );
          })}
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.gridScrollContent}
        >
          <View>
            <View style={[styles.gridHeaderRow, styles.gridHeadH]}>
              {Array.from({ length: grid.weeks }, (_, w) => (
                <Pressable
                  key={w}
                  style={styles.gridWeekHeadCell}
                  // ⛔ Only a STOP where it is a control. On Home (and on a
                  // one-week program) the head is a label, and turning every
                  // column into a button would add `weeks` announcements that
                  // say nothing a screen reader can act on.
                  {...(canRemoveWeek
                    ? {
                        accessibilityRole: "button" as const,
                        accessibilityLabel: `W${w + 1}`,
                        accessibilityHint: t("composer.holdWeekToRemoveHint", {
                          defaultValue: "Hold a week to remove it",
                        }),
                      }
                    : {
                        accessibilityElementsHidden: true,
                        importantForAccessibility:
                          "no-hide-descendants" as const,
                        focusable: false,
                      })}
                  {...weekRemoval(w)}
                >
                  <Text style={styles.gridWeekHead}>{`W${w + 1}`}</Text>
                </Pressable>
              ))}
            </View>

            {grid.days.map((day) => {
              const isSel = day.sessionIdx === selected;
              return (
                <View
                  key={day.sessionIdx}
                  style={[
                    styles.gridRow,
                    isSel && styles.gridRowSelected,
                    isSel && rowRing && styles.gridRowSelectedRing,
                    isSel && rowRing && styles.gridRowSelectedBody,
                  ]}
                >
                  <View style={styles.gridCells}>
                    {day.cells.map((cell, w) => {
                      const lit =
                        placement?.isPlaced(day.sessionIdx, w) ?? false;
                      return (
                        <GridCell
                          key={w}
                          cell={cell}
                          dayIdx={day.sessionIdx}
                          week={w}
                          theme={theme}
                          styles={styles}
                          interactive={cellsInteractive}
                          lit={lit}
                          isCursor={
                            selectedWeek === w && day.sessionIdx === selected
                          }
                          onPress={() =>
                            onSelectCell
                              ? onSelectCell(day.sessionIdx, w)
                              : onSelect(day.sessionIdx)
                          }
                          gesture={cellPlacement(day.sessionIdx, w)}
                          // The mark is a glyph, not colour — but a glyph says
                          // nothing to a screen reader, so the cell states in
                          // words what it is and whether the focused exercise
                          // sits on it.
                          a11yLabel={`${day.name} · W${w + 1}${
                            cell.band ? ` · ${bandLabel(t, cell.band)}` : ""
                          }${lit && placement ? ` — ${placement.label}` : ""}`}
                        />
                      );
                    })}
                  </View>
                </View>
              );
            })}
          </View>
        </ScrollView>
      </View>

      {/* ⛔ PE-31 — THE PLACEMENT SWITCH IS GONE, and nothing replaced it on
          screen. It used to sit here as a <ToggleRow> labelled with a
          COORDINATE ("D2 · W1"), which is why Antenore read it as a switch that
          removes a day (smoke 29/08) — and he had asked for the hold and only
          the hold. Its real job was the a11y door, because VoiceOver cannot
          reach onLongPress on iOS; that door moved ONTO the cell, as a named
          custom action that says the exercise out loud (useGridGestures'
          `cellPlacement`). Same reach, one control fewer, and the action now
          names WHAT is being placed instead of where. */}
      {hasStructure ? (
        <GridStructureRow
          theme={theme}
          styles={styles}
          onAddDay={onAddDay}
          onAddWeek={onAddWeek}
          canRemoveWeek={canRemoveWeek}
          canRemoveDay={canRemoveDay}
        />
      ) : null}

      <ScaleLegend
        variant="bands"
        theme={theme}
        includeFree
        style={styles.legendRow}
      />
      {/* ⛔ The hold is invisible until tried, so the line STATES it — the same
          rule GridStructureRow follows for the removal holds. With the
          <ToggleRow> gone this is the ONLY place the placement gesture is
          named, and a gesture nobody is told about is a gesture nobody has.
          It names the focused exercise, because that is what the hold acts on. */}
      <Text style={styles.gridHint}>
        {placement
          ? t("composer.placementHint", {
              exercise: placement.label,
              defaultValue: "Hold a cell to place or remove {{exercise}} there.",
            })
          : t("mobile.preview.gridHint", {
              defaultValue: "Tap a day to see its lifts.",
            })}
      </Text>
    </View>
  );
}
